/**
 * D-EZ-109 — THE PLAYOFF PICTURE AFTER RACE N, for the Wire's stakes lines.
 *
 * Regular season: who would be in the sixteen if the cut came today (the
 * season's own `seedPlayoff`, winners first), and the points at the cut.
 * Playoff: who is still alive (the seeds, less everyone knocked out in a round
 * that has finished by race N), points scored inside the round so far, and
 * where the round's cut falls. Only rounds whose last race is ≤ N are read.
 */

import type { HistorySeason } from '../history/source.ts';
import { seedPlayoff } from '../seasonPlay.ts';
import { ROUNDS, REGULAR_SEASON_RACES, type RoundSpec } from '../schedule.ts';
import type { SeasonStandings } from '../standings.ts';

export interface PlayoffPicture {
  phase: 'regular' | 'playoff';
  /** The round race N+1 belongs to. */
  round: RoundSpec;
  /** Regular season: in on today's table. Playoff: still alive. */
  inIds: Set<string>;
  /** Regular season: in on a win. */
  inOnWins: Set<string>;
  /** Regular season: the last man in on points, and his points. Playoff: the man on the cut, and his round points. */
  cut: { driverId: string; points: number } | null;
  /** Points inside the round so far (playoff only). */
  roundPoints: Map<string, number>;
  /** Playoff: the alive, best first by round points. */
  order: string[];
  /** Races left before the cut, race N+1 included. */
  racesToCut: number;
  /** True when race N+1 opens a new round (round points start from zero). */
  freshRound: boolean;
}

export function playoffPicture(season: HistorySeason, n: number, table: SeasonStandings, eligible: (id: string) => boolean): PlayoffPicture | null {
  const nextNumber = n + 1;
  const round = ROUNDS.find((r) => nextNumber >= r.firstRace && nextNumber <= r.lastRace);
  if (!round) return null;
  if (round.round === 'regular') {
    const seeds = seedPlayoff(table, eligible);
    const lastIn = [...seeds].reverse().find((s) => !s.viaWin) ?? null;
    return {
      phase: 'regular',
      round,
      inIds: new Set(seeds.map((s) => s.driverId)),
      inOnWins: new Set(seeds.filter((s) => s.viaWin).map((s) => s.driverId)),
      cut: lastIn ? { driverId: lastIn.driverId, points: lastIn.points } : null,
      roundPoints: new Map(),
      order: [],
      racesToCut: REGULAR_SEASON_RACES - nextNumber + 1,
      freshRound: false,
    };
  }
  const bracket = season.season.playoff;
  if (!bracket || n < REGULAR_SEASON_RACES) return null;
  const alive = new Set(bracket.seeds.map((s) => s.driverId));
  for (const r of bracket.rounds ?? []) {
    if (r.races.every((x) => x <= n)) for (const id of r.eliminated) alive.delete(id);
  }
  const roundRaces: number[] = [];
  for (let k = round.firstRace; k <= n; k++) roundRaces.push(k);
  const roundPoints = new Map<string, number>();
  for (const id of alive) roundPoints.set(id, roundRaces.reduce((t, k) => t + (table.byDriver[id]?.pointsByRace[k] ?? 0), 0));
  const order = [...alive].sort(
    (a, b) => (roundPoints.get(b) ?? 0) - (roundPoints.get(a) ?? 0) || (table.byDriver[b]?.points ?? 0) - (table.byDriver[a]?.points ?? 0) || a.localeCompare(b),
  );
  const at = round.advancing ?? 1;
  const cutId = order[at - 1] ?? null;
  return {
    phase: 'playoff',
    round,
    inIds: alive,
    inOnWins: new Set(),
    cut: cutId ? { driverId: cutId, points: roundPoints.get(cutId) ?? 0 } : null,
    roundPoints,
    order,
    racesToCut: round.lastRace - nextNumber + 1,
    freshRound: nextNumber === round.firstRace,
  };
}
